// Counts visits to the marketing site for the Bite admin "Web Activity" page.
// Each browser gets a random id (no account, no personal data); the
// recordLandingVisit Cloud Function counts it once per day as a unique visitor
// and every call as a page view. A plain fetch keeps the Firebase SDK out of
// the landing page bundle.
const ENDPOINT =
  "https://us-central1-bite-ecba0.cloudfunctions.net/recordLandingVisit";
const VISITOR_KEY = "bite-visitor-id";
const RECENT_KEY = "bite-landing-recent";
// Reloading the same page within this window isn't counted again.
const REPEAT_WINDOW_MS = 30 * 60 * 1000;
const BOT_PATTERN = /bot|crawl|spider|slurp|headless|lighthouse|preview|facebookexternalhit|embedly/i;

const enabled =
  process.env.NODE_ENV === "production" ||
  process.env.REACT_APP_ANALYTICS_DEV === "true";

const pageFor = (pathname) => {
  if (pathname === "/" || pathname === "") return "home";
  const first = pathname.split("/")[1];
  if (["privacy", "terms", "delete-account", "open"].includes(first)) return first;
  if (first === "u") return "user";
  if (first === "r") return "recipe";
  return "other";
};

const randomId = () => {
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 24);
};

const visitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id || !/^[a-z0-9]{16,40}$/.test(id)) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // Storage blocked: still count, as a one-off visitor.
    return randomId();
  }
};

/** Campaign tag (?utm_source / ?src / ?campaign), else the referring site. */
const sourceFor = () => {
  const params = new URLSearchParams(window.location.search);
  const tagged =
    params.get("utm_source") || params.get("src") || params.get("campaign") || params.get("utm_campaign");
  if (tagged) return tagged;
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    if (host && host !== window.location.hostname) {
      return host.replace(/^www\./, "").replace(/^(l|m|lm)\./, "").split(".")[0];
    }
  } catch {
    // Unparseable referrer: treat as direct.
  }
  return "direct";
};

const seenRecently = (page) => {
  try {
    const recent = JSON.parse(sessionStorage.getItem(RECENT_KEY) || "{}");
    const now = Date.now();
    if (recent[page] && now - recent[page] < REPEAT_WINDOW_MS) return true;
    recent[page] = now;
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // No session storage: count it.
  }
  return false;
};

export function trackLandingVisit(pathname) {
  if (!enabled || typeof window === "undefined") return;
  if (navigator.webdriver || BOT_PATTERN.test(navigator.userAgent || "")) return;
  const page = pageFor(pathname);
  if (seenRecently(page)) return;

  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: { visitorId: visitorId(), page, source: sourceFor() } }),
    keepalive: true,
  }).catch(() => {
    // Offline or blocked: analytics never affects the page.
  });
}
